import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { notesApi } from './notesApi';

const NotesAuthCtx = createContext(null);

export function NotesAuthProvider({ children }) {
  const [principal, setPrincipal] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem('notes_theme') || 'light');

  const refresh = useCallback(async () => {
    // Re-checks after the first load stay silent so protected pages never unmount.
    if (!loadedRef.current) setLoading(true);
    try {
      const { data } = await notesApi.get('/notes/auth/me');
      setPrincipal(data);
      loadedRef.current = true;
      // Sync theme from server preference on first resolve unless the user
      // already chose one locally.
      if (!localStorage.getItem('notes_theme') && data?.theme_pref) {
        setThemeState(data.theme_pref);
        localStorage.setItem('notes_theme', data.theme_pref);
      }
    } catch {
      setPrincipal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await notesApi.post('/notes/auth/login', { email, password });
    localStorage.setItem('notes_token', data.access_token);
    localStorage.setItem('notes_user', JSON.stringify(data.user));
    if (data.user?.theme_pref) {
      setThemeState(data.user.theme_pref);
      localStorage.setItem('notes_theme', data.user.theme_pref);
    }
    await refresh();
    return data.user;
  }, [refresh]);

  const logout = useCallback(() => {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    setPrincipal(null);
    loadedRef.current = false;
    window.location.href = '/login';
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    localStorage.setItem('notes_theme', next);
    notesApi.patch('/notes/auth/theme', { theme: next }).catch(() => {});
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      localStorage.setItem('notes_theme', next);
      notesApi.patch('/notes/auth/theme', { theme: next }).catch(() => {});
      return next;
    });
  }, []);

  // Persist the user's preferred default project (used by Smart Add / quick add).
  // Optimistically updates the local principal; reverts via refresh on failure.
  const updateDefaultProject = useCallback(async (projectId) => {
    const pid = projectId || null;
    setPrincipal((p) => (p ? { ...p, default_project_id: pid } : p));
    try {
      await notesApi.patch('/notes/auth/default-project', { project_id: pid });
      return true;
    } catch {
      refresh();
      return false;
    }
  }, [refresh]);

  return (
    <NotesAuthCtx.Provider value={{
      principal, loading, refresh, login, logout,
      theme, setTheme, toggleTheme, updateDefaultProject,
      isSuper: principal?.is_super === true,
    }}>
      {children}
    </NotesAuthCtx.Provider>
  );
}

export const useNotesAuth = () => useContext(NotesAuthCtx);
